import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Form, Row } from "antd";
import moment from "moment/moment";
import { useAppDispatch, useAppSelector } from "../../../../index";
import {
  fetchIndividualVersions,
  getIndividualInfoById,
  updateContactIndividual,
} from "../../../../store/slices/accountsTs";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { checkIsLoaded, checkIsLoading, useActiveModal } from "../../../../lib/utils";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import { toDisplayFormat, toHttpFormatStartOfDay } from "../../../../lib/utils/formatDate";
import ToggleableVersionsTable from "../../../ToggleableVersionsTable/ToggleableVersionsTable";
import HistoryAndPropertyModal from "../../../HistoryAndPropertyModal/HistoryAndPropertyModal";
import ChangeVersionsTable from "../../../common/ChangeVersionsTable";
import { TableRecord } from "../../../../lib/interfaces";
import { sexDictionary } from "../../../../lib/const/sexDictionary";

export const AccountContactsDetailsPersonal = () => {
  const history = useHistory();
  const { activeModal, setActiveModal, activeModalOptions } = useActiveModal("");
  const individualId = history.location.search.substring(1).split("=")[1];
  const {
    getIndividualByIdData,
    getIndividualByIdLoading,
    individualVersions,
    individualVersionsLoading,
  } = useAppSelector((state) => state.accountsTs);

  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  const [form] = Form.useForm();
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);
  const dispatch = useAppDispatch();

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    firstName: "15%",
    middleName: "15%",
    lastName: "20%",
    sex: "15%",
    period: "20%",
    actions: "15%",
  };

  useEffect(() => {
    if (checkIsLoaded(getIndividualByIdLoading)) {
      setTableData([
        {
          key: getIndividualByIdData?.id,
          id: getIndividualByIdData?.id,
          firstName: getIndividualByIdData?.firstName,
          middleName: getIndividualByIdData?.middleName,
          lastName: getIndividualByIdData?.lastName,
          fromDate: getIndividualByIdData?.fromDate,
          toDate: getIndividualByIdData?.toDate,
          sex: getIndividualByIdData?.sex,
        },
      ]);
    }
  }, [getIndividualByIdData]);

  useEffect(() => {
    if (checkIsLoaded(individualVersionsLoading)) {
      setChildTableData(
        individualVersions
          .map((item, i) => {
            return {
              id: item.id,
              firstName: item.firstName,
              middleName: item.middleName,
              lastName: item.lastName,
              fromDate: item.fromDate,
              toDate: item.toDate,
              sex: item.sex,
              period: item,
              ...versionsTableUtils.getCommonChildTableProps(item, i, individualVersions.length),
            };
          })
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
  }, [individualVersions]);

  const showHistoryModal = (rec) => {
    setActiveModal("accountContactsDetailsPersonalHistoryAndPropertyModal", rec?.id);
  };

  const handleHideModal = () => {
    setActiveModal("", undefined);
  };

  const parseSex = (name: string): string => {
    let parseString;
    if (name === "M") {
      parseString = "Мужской";
    } else {
      parseString = "Женский";
    }
    return parseString;
  };

  const [columns, setColumns] = useState([
    {
      title: "Фамилия",
      dataIndex: "lastName",
      key: "lastName",
      width: columnsWidths.lastName,
      hidden: false,
    },
    {
      title: "Имя",
      dataIndex: "firstName",
      key: "firstName",
      width: columnsWidths.firstName,
      hidden: false,
    },
    {
      title: "Отчество",
      dataIndex: "middleName",
      key: "middleName",
      width: columnsWidths.middleName,
      hidden: false,
    },
    {
      title: "Пол",
      dataIndex: "sex",
      key: "sex",
      width: columnsWidths.sex,
      hidden: false,
      render: (record) => parseSex(record),
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (value, record) => {
        if (record.fromDate && record.toDate) {
          return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
        }
        return "";
      },
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
      render: (value, record) => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(record);
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        </div>
      ),
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Фамилия",
      dataIndex: "lastName",
      key: "lastName",
      width: columnsWidths.lastName,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="lastName"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Имя",
      dataIndex: "firstName",
      key: "firstName",
      width: columnsWidths.firstName,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="firstName"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Отчество",
      dataIndex: "middleName",
      key: "middleName",
      width: columnsWidths.middleName,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="middleName"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required={false}
        />
      ),
    },
    {
      title: "Пол",
      dataIndex: "sex",
      key: "sex",
      width: columnsWidths.sex,
      hidden: false,
      render: (value, record) => (
        <ChangeVersionsTable.Select
          dic={sexDictionary}
          name="sex"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (text, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
          toDateDisabled
          initialToDate={moment()}
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        //  const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              // TODO пока захардкожены hasAccess=true
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: (value, record) => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal(record);
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        </div>
      ),
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden]);

  const onFinish = (values) => {
    const changeableRecord: TableRecord = versionsTableUtils.getChangeableRecord(childTableData);
    const isForceUpdate = Boolean(changeableRecord.isEditing);
    const fromDate = isForceUpdate
      ? changeableRecord.fromDate
      : toHttpFormatStartOfDay(values.fromDate);

    const data = {
      individualId: changeableRecord?.id,
      fromDate,
      force: isForceUpdate,
      body: {
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        sex: values.sex,
      },
    };
    dispatch(updateContactIndividual(data)).then(({ payload }) => {
      if (payload) {
        dispatch(getIndividualInfoById({ individualId }));
        dispatch(fetchIndividualVersions({ individualId: Number(individualId) }));
      }
    });
  };

  return (
    <div className="account-details-table-wrapper">
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          title={
            <Row align="middle">
              <h4 style={{ margin: 0 }}>Личные данные</h4>
            </Row>
          }
          tableName="account-list-individual-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          childTableData={childTableData}
          isToggleableColumns={false}
          isChildTableLoading={checkIsLoading(individualVersionsLoading)}
          onOpenVersionsCallback={(record, expanded) => {
            if (!expanded) {
              dispatch(fetchIndividualVersions({ individualId: record?.id }));
            }
          }}
          isPageable={false}
        />
      </Form>
      <HistoryAndPropertyModal
        objectTypeSysname="individual"
        objectId={Number(activeModalOptions)}
        handleHideModal={handleHideModal}
        visibleModal={activeModal === "accountContactsDetailsPersonalHistoryAndPropertyModal"}
        // hasAccessToEdit={}
      />
    </div>
  );
};
